import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Camera, DollarSign, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const CheckOutModal = ({ isOpen, onClose, rental, truck, onSuccess, language = 'pt-BR' }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    km_final: '',
    combustivel_final: '',
    observacoes: '',
    danos_reportados: '',
    metodo_pagamento: 'desconto_payroll'
  });
  const [fotos, setFotos] = useState([]);
  const [valorCalculado, setValorCalculado] = useState(0);
  const [valorFinal, setValorFinal] = useState(0);
  const [diasAlugados, setDiasAlugados] = useState(0);
  const [motivoAjuste, setMotivoAjuste] = useState('');

  useEffect(() => {
    if (rental?.check_in?.data) {
      const inicio = new Date(rental.check_in.data);
      const fim = new Date();
      const dias = Math.floor((fim - inicio) / (1000 * 60 * 60 * 24)) + 1;
      setDiasAlugados(dias);
      
      const valorDiario = rental.financeiro?.valor_diario || 100;
      const calc = dias * valorDiario;
      const custos = rental.total_custos_adicionais || 0;
      const total = calc + custos;
      
      setValorCalculado(calc);
      setValorFinal(total);
    }
  }, [rental]);

  const handleSubmit = async () => {
    if (!formData.km_final || !formData.combustivel_final) {
      toast.error(language === 'pt-BR' ? 'Preencha KM final e combustível' : 'Fill in final KM and fuel');
      return;
    }

    setLoading(true);
    try {
      const token = sessionStorage.getItem('access_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      await axios.put(`${API_URL}/api/rentals/${rental.id}/checkout`, {
        km_final: parseFloat(formData.km_final),
        combustivel_final: formData.combustivel_final,
        fotos: fotos,
        observacoes: formData.observacoes,
        danos_reportados: formData.danos_reportados,
        valor_final: valorFinal,
        motivo_ajuste: motivoAjuste,
        metodo_pagamento: formData.metodo_pagamento
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(language === 'pt-BR' ? '🏁 Check-out realizado!' : '🏁 Check-out completed!');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Erro:', error);
      toast.error(language === 'pt-BR' ? 'Erro no check-out' : 'Check-out error');
    } finally {
      setLoading(false);
    }
  };

  if (!rental || !truck) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>🏁 Check-out - {truck.modelo} ({truck.placa})</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-gray-50 p-3 rounded">
            <p className="font-semibold">PM: {rental.pm_name}</p>
            <p className="text-sm text-gray-600">
              {language === 'pt-BR' ? 'Período:' : 'Period:'} {new Date(rental.check_in.data).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })} - {new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}
            </p>
          </div>

          <div className="bg-blue-50 p-4 rounded border border-blue-200">
            <p className="font-semibold text-blue-800 mb-2">📊 {language === 'pt-BR' ? 'CÁLCULO:' : 'CALCULATION:'}</p>
            <div className="space-y-1 text-sm">
              <p>├─ {language === 'pt-BR' ? 'Dias alugados:' : 'Days rented:'} <strong>{diasAlugados} {language === 'pt-BR' ? 'dias' : 'days'}</strong></p>
              <p>├─ {language === 'pt-BR' ? 'Aluguel:' : 'Rental:'} {diasAlugados} × $100 = <strong>${valorCalculado.toFixed(2)}</strong></p>
              <p>├─ {language === 'pt-BR' ? 'Custos reportados:' : 'Reported costs:'} <strong>${(rental.total_custos_adicionais || 0).toFixed(2)}</strong></p>
              <p className="border-t pt-1 mt-1">└─ <strong>{language === 'pt-BR' ? 'Total calculado:' : 'Calculated total:'} ${(valorCalculado + (rental.total_custos_adicionais || 0)).toFixed(2)}</strong></p>
            </div>
          </div>

          {rental.total_atualizacoes > 0 && (
            <div className="bg-yellow-50 p-3 rounded border border-yellow-200 text-sm">
              <p className="font-semibold text-yellow-800">🗒️ {rental.total_atualizacoes} {language === 'pt-BR' ? 'atualizações reportadas' : 'updates reported'}</p>
              <p className="text-xs text-yellow-700 mt-1">{language === 'pt-BR' ? 'Custos adicionais incluídos no total' : 'Additional costs included in total'}</p>
            </div>
          )}

          <div>
            <Label>💵 {language === 'pt-BR' ? 'Valor Final (Editável)' : 'Final Value (Editable)'} *</Label>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold">$</span>
              <Input 
                type="number"
                step="0.01"
                value={valorFinal}
                onChange={(e) => setValorFinal(parseFloat(e.target.value) || 0)}
                className="text-lg font-bold"
              />
            </div>
            {valorFinal !== (valorCalculado + (rental.total_custos_adicionais || 0)) && (
              <div className="mt-2">
                <Label className="text-xs">{language === 'pt-BR' ? 'Motivo do ajuste:' : 'Reason for adjustment:'}</Label>
                <Input 
                  value={motivoAjuste}
                  onChange={(e) => setMotivoAjuste(e.target.value)}
                  placeholder={language === 'pt-BR' ? 'Ex: Desconto por devolução antecipada' : 'E.g., Early return discount'}
                  className="text-sm"
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{language === 'pt-BR' ? 'KM Final' : 'Final KM'} *</Label>
              <Input 
                type="number"
                value={formData.km_final}
                onChange={(e) => setFormData({...formData, km_final: e.target.value})}
                placeholder={rental.check_in?.km_inicial?.toString()}
              />
              {formData.km_final && (
                <p className="text-xs text-gray-500 mt-1">
                  {language === 'pt-BR' ? 'Rodou:' : 'Driven:'} {(parseFloat(formData.km_final) - rental.check_in.km_inicial).toFixed(0)} km
                </p>
              )}
            </div>

            <div>
              <Label>{language === 'pt-BR' ? 'Combustível Final' : 'Final Fuel'} *</Label>
              <Select value={formData.combustivel_final} onValueChange={(v) => setFormData({...formData, combustivel_final: v})}>
                <SelectTrigger>
                  <SelectValue placeholder={language === 'pt-BR' ? 'Selecione' : 'Select'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Vazio">⛽ {language === 'pt-BR' ? 'Vazio' : 'Empty'}</SelectItem>
                  <SelectItem value="1/4">⛽ 1/4</SelectItem>
                  <SelectItem value="1/2">⛽ 1/2</SelectItem>
                  <SelectItem value="3/4">⛽ 3/4</SelectItem>
                  <SelectItem value="Cheio">⛽ {language === 'pt-BR' ? 'Cheio' : 'Full'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>{language === 'pt-BR' ? 'Observações' : 'Observations'}</Label>
            <Textarea 
              value={formData.observacoes}
              onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
              rows={2}
            />
          </div>

          <div>
            <Label>{language === 'pt-BR' ? 'Método de Pagamento' : 'Payment Method'} *</Label>
            <Select value={formData.metodo_pagamento} onValueChange={(v) => setFormData({...formData, metodo_pagamento: v})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="desconto_payroll">💳 {language === 'pt-BR' ? 'Descontar do Payroll' : 'Deduct from Payroll'}</SelectItem>
                <SelectItem value="pagamento_separado">💵 {language === 'pt-BR' ? 'Pagamento Separado' : 'Separate Payment'}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {language === 'pt-BR' ? 'Cancelar' : 'Cancel'}
          </Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-green-600 hover:bg-green-700">
            {loading ? (language === 'pt-BR' ? 'Processando...' : 'Processing...') : (language === 'pt-BR' ? '✅ Finalizar Check-out' : '✅ Complete Check-out')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CheckOutModal;